import { useState, useEffect } from "react";
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  TrendingUp,
  Calendar,
  Tag,
  Eye,
  MoreHorizontal,
} from "lucide-react";
import medicalDB from "../services/database";
import DraftAnalytics from "./DraftAnalytics";

const DraftManager = () => {
  const [drafts, setDrafts] = useState([]);
  const [filteredDrafts, setFilteredDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortBy, setSortBy] = useState("updated");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [viewingDraft, setViewingDraft] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Draft form state
  const [draftForm, setDraftForm] = useState({
    name: "",
    description: "",
    category: "General",
    template: {
      complaints: [],
      vitalSigns: { temp: null, bp: null, hr: null, spo2: null, weight: null },
      allergies: [],
      infections: [],
      pastHistory: [],
      notes: "",
      takenDrugs: [],
      prescription: [],
      administeredMeds: [],
    },
  });

  const categories = [
    "General",
    "Preventive Care",
    "Acute Care",
    "Chronic Care",
    "Emergency",
    "Specialty",
  ];

  useEffect(() => {
    loadDrafts();
  }, []);

  useEffect(() => {
    filterAndSortDrafts();
  }, [drafts, searchTerm, selectedCategory, sortBy]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      const allDrafts = await medicalDB.getAllDrafts({ sortBy });
      setDrafts(allDrafts);
    } catch (error) {
      console.error("Error loading drafts:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortDrafts = () => {
    let filtered = [...drafts];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (draft) =>
          draft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          draft.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(
        (draft) => draft.category === selectedCategory
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "category":
          return a.category.localeCompare(b.category);
        case "usage":
          return (b.usageCount || 0) - (a.usageCount || 0);
        case "created":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "updated":
        default:
          return new Date(b.updatedAt) - new Date(a.updatedAt);
      }
    });

    setFilteredDrafts(filtered);
  };

  const handleCreateDraft = async () => {
    try {
      if (!draftForm.name.trim()) {
        alert("Please enter a draft name");
        return;
      }

      await medicalDB.createDraft(draftForm);
      setShowCreateForm(false);
      resetForm();
      loadDrafts();
    } catch (error) {
      console.error("Error creating draft:", error);
      alert("Failed to create draft. Please try again.");
    }
  };

  const handleUpdateDraft = async () => {
    try {
      if (!draftForm.name.trim()) {
        alert("Please enter a draft name");
        return;
      }

      await medicalDB.updateDraft(editingDraft.id, draftForm);
      setEditingDraft(null);
      resetForm();
      loadDrafts();
    } catch (error) {
      console.error("Error updating draft:", error);
      alert("Failed to update draft. Please try again.");
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (
      !confirm(
        "Are you sure you want to delete this draft? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await medicalDB.deleteDraft(draftId);
      loadDrafts();
    } catch (error) {
      console.error("Error deleting draft:", error);
      alert("Failed to delete draft. Please try again.");
    }
  };

  const resetForm = () => {
    setDraftForm({
      name: "",
      description: "",
      category: "General",
      template: {
        complaints: [],
        vitalSigns: {
          temp: null,
          bp: null,
          hr: null,
          spo2: null,
          weight: null,
        },
        allergies: [],
        infections: [],
        pastHistory: [],
        notes: "",
        takenDrugs: [],
        prescription: [],
        administeredMeds: [],
      },
    });
  };

  const startEdit = (draft) => {
    setEditingDraft(draft);
    setDraftForm({
      name: draft.name,
      description: draft.description,
      category: draft.category,
      template: draft.template,
    });
    setShowCreateForm(true);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading drafts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <FileText className="h-6 w-6 text-medical-blue" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Visit Draft Templates
            </h2>
            <p className="text-sm text-gray-600">
              Manage reusable visit templates to speed up data entry
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingDraft(null);
            resetForm();
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>New Template</span>
        </button>
      </div>

      {/* Analytics Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setShowAnalytics(!showAnalytics)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-medical-blue" />
            <span className="font-medium text-gray-900">
              Analytics & Insights
            </span>
          </div>
          <div className="text-gray-400">{showAnalytics ? "−" : "+"}</div>
        </button>
        {showAnalytics && (
          <div className="border-t border-gray-200 p-4">
            <DraftAnalytics />
          </div>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search drafts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 form-input"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 form-input"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="form-input"
            >
              <option value="updated">Recently Updated</option>
              <option value="created">Recently Created</option>
              <option value="name">Name A-Z</option>
              <option value="category">Category</option>
              <option value="usage">Most Used</option>
            </select>
          </div>

          {/* Results Count */}
          <div className="flex items-center text-sm text-gray-600">
            <span>
              {filteredDrafts.length} of {drafts.length} templates
            </span>
          </div>
        </div>
      </div>

      {/* Drafts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDrafts.map((draft) => (
          <div
            key={draft.id}
            className="card hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 line-clamp-1">
                  {draft.name}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                  {draft.description}
                </p>
              </div>
              <div className="relative">
                <button className="text-gray-400 hover:text-gray-600 p-1">
                  <MoreHorizontal size={16} />
                </button>
              </div>
            </div>

            {/* Category Badge */}
            <div className="flex items-center space-x-2 mb-3">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-medical-lightblue text-medical-blue">
                <Tag size={12} className="mr-1" />
                {draft.category}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <TrendingUp size={12} className="mr-1" />
                {draft.usageCount || 0} uses
              </span>
            </div>

            {/* Template Preview */}
            <div className="text-xs text-gray-500 mb-3">
              <div className="flex items-center space-x-4">
                {draft.template.complaints?.length > 0 && (
                  <span>{draft.template.complaints.length} complaints</span>
                )}
                {draft.template.prescription?.length > 0 && (
                  <span>
                    {draft.template.prescription.length} prescriptions
                  </span>
                )}
                {draft.template.takenDrugs?.length > 0 && (
                  <span>{draft.template.takenDrugs.length} medications</span>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
              <div className="flex items-center">
                <Calendar size={12} className="mr-1" />
                {formatDate(draft.updatedAt)}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewingDraft(draft)}
                className="flex-1 text-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Eye size={14} className="inline mr-1" />
                Preview
              </button>
              <button
                onClick={() => startEdit(draft)}
                className="flex-1 text-center px-3 py-2 text-sm bg-medical-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit2 size={14} className="inline mr-1" />
                Edit
              </button>
              <button
                onClick={() => handleDeleteDraft(draft.id)}
                className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredDrafts.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No templates found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedCategory
              ? "Try adjusting your filters"
              : "Get started by creating your first visit template"}
          </p>
          {!searchTerm && !selectedCategory && (
            <div className="mt-6">
              <button
                onClick={() => {
                  setShowCreateForm(true);
                  setEditingDraft(null);
                  resetForm();
                }}
                className="btn-primary"
              >
                <Plus size={16} className="mr-2" />
                Create Your First Template
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Draft Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingDraft ? "Edit Template" : "Create New Template"}
              </h3>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="form-label">Template Name *</label>
                <input
                  type="text"
                  value={draftForm.name}
                  onChange={(e) =>
                    setDraftForm({ ...draftForm, name: e.target.value })
                  }
                  placeholder="e.g., Routine Checkup"
                  className="form-input"
                  required
                />
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={draftForm.description}
                  onChange={(e) =>
                    setDraftForm({ ...draftForm, description: e.target.value })
                  }
                  placeholder="Brief description of when to use this template"
                  rows={3}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">Category</label>
                <select
                  value={draftForm.category}
                  onChange={(e) =>
                    setDraftForm({ ...draftForm, category: e.target.value })
                  }
                  className="form-input"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Default Notes</label>
                <textarea
                  value={draftForm.template.notes}
                  onChange={(e) =>
                    setDraftForm({
                      ...draftForm,
                      template: {
                        ...draftForm.template,
                        notes: e.target.value,
                      },
                    })
                  }
                  placeholder="Default notes for this type of visit"
                  rows={4}
                  className="form-input"
                />
              </div>

              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                <p className="font-medium text-blue-900 mb-1">
                  Template Configuration
                </p>
                <p>
                  For now, you can set basic template information. Detailed
                  field configuration (complaints, medications, etc.) will be
                  available in the advanced editor or can be created by saving
                  an existing visit as a template.
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingDraft(null);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={editingDraft ? handleUpdateDraft : handleCreateDraft}
                className="btn-primary"
              >
                {editingDraft ? "Update Template" : "Create Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Draft Modal */}
      {viewingDraft && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {viewingDraft.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {viewingDraft.description}
                  </p>
                </div>
                <button
                  onClick={() => setViewingDraft(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Category:</span>{" "}
                  {viewingDraft.category}
                </div>
                <div>
                  <span className="font-medium">Usage Count:</span>{" "}
                  {viewingDraft.usageCount || 0}
                </div>
                <div>
                  <span className="font-medium">Created:</span>{" "}
                  {formatDate(viewingDraft.createdAt)}
                </div>
                <div>
                  <span className="font-medium">Updated:</span>{" "}
                  {formatDate(viewingDraft.updatedAt)}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  Template Content
                </h4>
                <div className="space-y-3 text-sm">
                  {viewingDraft.template.complaints?.length > 0 && (
                    <div>
                      <span className="font-medium">Complaints:</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {viewingDraft.template.complaints.map(
                          (complaint, idx) => (
                            <span
                              key={idx}
                              className="inline-block px-2 py-1 bg-gray-100 rounded text-xs"
                            >
                              {complaint}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {viewingDraft.template.notes && (
                    <div>
                      <span className="font-medium">Default Notes:</span>
                      <p className="mt-1 text-gray-600">
                        {viewingDraft.template.notes}
                      </p>
                    </div>
                  )}

                  {viewingDraft.template.prescription?.length > 0 && (
                    <div>
                      <span className="font-medium">Prescriptions:</span>
                      <div className="mt-1 space-y-1">
                        {viewingDraft.template.prescription.map((med, idx) => (
                          <div
                            key={idx}
                            className="text-xs bg-gray-50 p-2 rounded"
                          >
                            {med.name} - {med.dose}
                            {med.unit} ({med.duration})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingDraft.template.takenDrugs?.length > 0 && (
                    <div>
                      <span className="font-medium">Current Medications:</span>
                      <div className="mt-1 space-y-1">
                        {viewingDraft.template.takenDrugs.map((med, idx) => (
                          <div
                            key={idx}
                            className="text-xs bg-gray-50 p-2 rounded"
                          >
                            {med.name} - {med.dose}
                            {med.unit}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setViewingDraft(null)}
                className="btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setViewingDraft(null);
                  startEdit(viewingDraft);
                }}
                className="btn-primary"
              >
                Edit Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftManager;
